import { Component, type ErrorInfo, type ReactNode } from 'react';
import { AlertTriangle, RotateCcw, Home } from 'lucide-react';

interface ErrorBoundaryProps {
  children: ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
}

/**
 * Top-level error boundary. Catches any uncaught render error from the entire
 * app tree (including providers and the router) and shows a friendly fallback
 * with a reload action, instead of white-screening the whole site.
 *
 * Intentionally dependency-free: it reads language/direction straight from the
 * document and uses inline bilingual copy, because the thing that crashed could
 * be i18n or one of the context providers — so we can't rely on `useTranslation`
 * or any app context being usable inside the fallback.
 */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  state: ErrorBoundaryState = { hasError: false };

  static getDerivedStateFromError(): ErrorBoundaryState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Surface the error in the console for debugging. A future improvement is to
    // forward this to an error-aggregation service (Sentry/APM — see Op 2).
    console.error('Uncaught application error:', error, errorInfo);
  }

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    const isArabic = (document.documentElement.lang || '').toLowerCase().startsWith('ar');
    const dir = isArabic ? 'rtl' : 'ltr';

    const copy = isArabic
      ? {
          title: 'حدث خطأ غير متوقع',
          body: 'نعتذر، حدث خطأ ما. يرجى إعادة تحميل الصفحة. إذا استمرت المشكلة، حاول مرة أخرى لاحقًا.',
          reload: 'إعادة تحميل الصفحة',
          home: 'العودة للرئيسية',
        }
      : {
          title: 'Something went wrong',
          body: "Sorry, an unexpected error occurred. Please reload the page. If the problem persists, try again later.",
          reload: 'Reload page',
          home: 'Back to home',
        };

    return (
      <div
        dir={dir}
        className="min-h-screen flex items-center justify-center bg-white px-4"
      >
        <div className="w-full max-w-md rounded-2xl border border-slate-200 bg-white/80 backdrop-blur-sm shadow-xl p-8 text-center">
          <div className="mx-auto mb-6 flex h-16 w-16 items-center justify-center rounded-full bg-red-50">
            <AlertTriangle className="h-8 w-8 text-red-500" aria-hidden="true" />
          </div>
          <h1 className="mb-3 text-xl font-bold text-[var(--pv-color-primary)]">{copy.title}</h1>
          <p className="mb-8 text-sm leading-relaxed text-slate-600">{copy.body}</p>
          <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
            <button
              type="button"
              onClick={this.handleReload}
              className="inline-flex items-center justify-center gap-2 rounded-xl bg-emerald-600 px-5 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            >
              <RotateCcw className="h-4 w-4" aria-hidden="true" />
              {copy.reload}
            </button>
            <a
              href={import.meta.env.BASE_URL || '/'}
              className="inline-flex items-center justify-center gap-2 rounded-xl border border-slate-300 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60"
            >
              <Home className="h-4 w-4" aria-hidden="true" />
              {copy.home}
            </a>
          </div>
        </div>
      </div>
    );
  }
}

export default ErrorBoundary;
